import {
  AssetSubClassNames,
  Entity,
  MappedEntity,
  mapAssetClass,
  PrimaryMethod,
  SnakeMeta,
} from '@api';
import {
  getTickerKey,
  PriceStatus,
  PriceUpdate,
  priceDecimals,
  TickerPayload,
} from '@helpers';
import {
  getPriceChange,
  getTickerForLivePrice,
} from '@terminal/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';
import { StoreApi } from 'zustand';

export enum Identifier {
  Global = 'Global',
  Watchlist = 'Watchlist',
}

export interface LivePriceChange {
  status: PriceStatus;
  change: string;
  proportionChange: string;
}

export interface LivePriceInfo {
  price: number;
  priceChange: LivePriceChange;
  decimals: number;
  time: string;
  lastKnownPrice: number;
  isLive: boolean;
  priceBefore: number;
}

export interface LivePriceData {
  [key: string]: LivePriceInfo;
}

export type SubscribeConfig = {
  priceEntities: PriceEntity[];
  identifier?: Identifier;
  delayedPrices: boolean;
};

export type SubscribeFn = (config: SubscribeConfig) => () => void;

export interface LivePriceState {
  livePriceData: LivePriceData;
  componentsWatchingData: { [key: string]: string[] };
  subscribe: SubscribeFn;
}

export interface PriceEntity {
  entity: {
    ticker: string;
    sub_class: MappedEntity['sub_class'];
    primary_method: PrimaryMethod | '';
    subscribable_ticker?: string;
    trading_country: Entity['trading_country'];
    exchange?: string | { code: string };
  };
  lastKnownPrice: number;
  priceBefore: number;
  time?: string;
}

export const getPriceEntityExchange = ({
  exchange,
}: Pick<PriceEntity['entity'], 'exchange'>): string | undefined =>
  typeof exchange === 'string' ? exchange : exchange?.code;

export const getLivePriceDataKey = (
  entity: Pick<PriceEntity['entity'], 'subscribable_ticker' | 'exchange'> & {
    trading_country?: Entity['trading_country'];
  }
): string | undefined => {
  const ticker = getTickerForLivePrice(entity);

  if (!ticker) {
    return undefined;
  }

  return getTickerKey({
    ticker,
    trading_country: entity.trading_country?.long ?? '',
    exchange: getPriceEntityExchange(entity),
  });
};

export const findLivePriceDataKeyForUpdate = (
  livePriceData: LivePriceData,
  { ticker, exchange }: Pick<PriceUpdate, 'ticker' | 'exchange'>
): string | undefined => {
  const matchingKeys = Object.keys(livePriceData).filter(key => {
    const [keyTicker] = key.split('|');

    return keyTicker === ticker;
  });

  if (matchingKeys.length === 0) {
    return undefined;
  }

  const exactExchangeMatch = matchingKeys.find(key => {
    const [, , keyExchange = ''] = key.split('|');

    return keyExchange.toLowerCase() === exchange.toLowerCase();
  });

  return exactExchangeMatch ?? matchingKeys[0];
};

export const mapLivePriceData = ({
  entity,
  lastKnownPrice,
  priceBefore,
  time = '',
}: PriceEntity) => {
  const decimals = priceDecimals(entity.sub_class as AssetSubClassNames)(
    lastKnownPrice
  );

  return {
    price: lastKnownPrice,
    decimals,
    priceChange: getPriceChange({
      lastPrice: priceBefore,
      newPrice: lastKnownPrice,
      decimals,
      isPrice: entity.primary_method === PrimaryMethod.Price,
    }).priceChange,
    lastKnownPrice,
    time,
    isLive: false,
    priceBefore,
  };
};

export const mapPriceEntityToLivePriceData = (
  priceEntities: PriceEntity[],
  livePriceData: LivePriceData
) => {
  return (
    priceEntities
      // Some charts dont have a snake meta, but try to use the WS resulting in saving invalid data in livePriceData
      // Filter out entities without snake meta to avoid
      .filter(entity => entity.lastKnownPrice !== 0)
      .reduce(
        ({ data, tickers }, priceEntity) => {
          const entity = priceEntity.entity;
          const tradingCountry = entity.trading_country;
          const tickerForLivePrice = getTickerForLivePrice(entity);
          const livePriceDataKey = getLivePriceDataKey(entity);

          const existingEntry = livePriceDataKey
            ? data[livePriceDataKey]
            : undefined;

          if (tickerForLivePrice && livePriceDataKey && !existingEntry) {
            const updatedTickers = [
              ...tickers,
              {
                ticker: tickerForLivePrice,
                trading_country: tradingCountry?.long ?? '',
                exchange: getPriceEntityExchange(entity),
              },
            ];

            return {
              data: {
                ...data,
                [livePriceDataKey]: mapLivePriceData(priceEntity),
              },
              tickers: updatedTickers,
            };
          }

          const shouldUpdateBaseline =
            existingEntry?.lastKnownPrice !== undefined &&
            existingEntry.lastKnownPrice !== priceEntity.lastKnownPrice;

          if (tickerForLivePrice && livePriceDataKey && shouldUpdateBaseline) {
            const updatedTickers = [
              ...tickers,
              {
                ticker: tickerForLivePrice,
                trading_country: tradingCountry?.long ?? '',
                exchange: getPriceEntityExchange(entity),
              },
            ];

            return {
              data: {
                ...data,
                [livePriceDataKey]: {
                  ...existingEntry,
                  ...mapLivePriceData(priceEntity),
                  isLive: existingEntry.isLive,
                },
              },
              tickers: updatedTickers,
            };
          }

          return { data, tickers };
        },
        { data: livePriceData, tickers: [] as TickerPayload[] }
      )
  );
};

export const mapNewLivePriceDataToWatchedData = (
  priceEntities: PriceEntity[],
  newLivePriceData: LivePriceData,
  identifier: Identifier,
  componentsWatchingData: { [key: string]: string[] }
) => {
  const allEntityKeys = priceEntities
    .map(({ entity }) => getLivePriceDataKey(entity))
    .filter((key): key is string => !!key);
  return Object.keys(newLivePriceData).reduce((acc: any, key) => {
    if (!allEntityKeys.includes(key)) {
      return acc;
    }

    if (acc?.[key] && !acc[key].includes(identifier)) {
      acc[key].push(identifier);
    } else {
      acc[key] = [identifier];
    }

    return acc;
  }, componentsWatchingData);
};

export const priceUpdater =
  (set: StoreApi<LivePriceState>['setState']) =>
  ({ price, time, ticker, exchange }: PriceUpdate) => {
    set(state => {
      const livePriceDataKey = findLivePriceDataKeyForUpdate(
        state.livePriceData,
        { ticker, exchange }
      );
      const tickerData = livePriceDataKey
        ? state.livePriceData[livePriceDataKey]
        : undefined;

      if (!livePriceDataKey || !tickerData) {
        return state;
      }

      const lastKnownPrice = tickerData.lastKnownPrice;

      const { priceChange } = getPriceChange({
        lastPrice: lastKnownPrice,
        newPrice: price,
        decimals: tickerData.decimals,
      });

      return {
        ...state,
        livePriceData: {
          ...state.livePriceData,
          [livePriceDataKey]: {
            price,
            priceChange,
            lastKnownPrice,
            isLive: true,
            time,
            priceBefore: tickerData.priceBefore,
            decimals: tickerData.decimals,
          },
        },
      };
    });
  };

export const updateWatching = (
  set: StoreApi<LivePriceState>['setState'],
  get: StoreApi<LivePriceState>['getState'],
  identifier: Identifier,
  livePriceDataKey?: string
) => {
  if (!livePriceDataKey) {
    return false;
  }

  if (get().componentsWatchingData[livePriceDataKey]?.length > 1) {
    const newWatching = get().componentsWatchingData[livePriceDataKey];

    set({
      componentsWatchingData: {
        ...get().componentsWatchingData,
        [livePriceDataKey]: newWatching.filter(
          (id: string) => id !== identifier
        ),
      },
    });
    return false;
  } else {
    const { [livePriceDataKey]: _, ...rest } = get().componentsWatchingData;
    set({
      componentsWatchingData: rest,
    });
    return true;
  }
};

export const mapEntityToPriceEntity = (
  entity?: Entity,
  snakeMeta?: SnakeMeta
): PriceEntity[] =>
  entity && snakeMeta?.last_value
    ? [
        {
          entity: {
            ticker: entity.ticker,
            sub_class: mapAssetClass(entity).sub_class,
            primary_method: entity.primary_method,
            subscribable_ticker: entity.subscribable_ticker,
            trading_country: entity.trading_country,
            exchange: entity.exchange.code,
          },
          lastKnownPrice: snakeMeta.last_value,
          priceBefore: snakeMeta.before_last_value,
          time: new Date(snakeMeta.last_timestamp / 100_0000).toISOString(),
        },
      ]
    : [];
